import { toast } from "sonner";
import { routePathsByRole } from "../../../constants";
import { registerAccount, startSession, generatedPassword, homePathFor } from "./authV2Services";
import { clearSignup, writeSignup } from "./signupState";
import { accountTypeOf, isAccountType } from "./accountTypes";
import { sentence } from "./serverText";

// Every first sign-up needs a verified ticket before the account exists: a
// typed email or mobile number earns it with a code, Google hands one over
// at once. With the ticket, every account type goes on to the name step
// (athletes then add their birthday). With no type yet (Google from the log
// in screen, or a code confirmed before a pick) the type screen asks.
export const routeAfterVerify = (state = {}) => (isAccountType(state.accountType) ? "/signup/name" : "/register");

export const isVerified = (state = {}) => !!(state.ticket && state.email);

// A typed sign-up whose code proved only the mobile (a text code): the
// email stays unproven until its owner confirms it (verify step's
// `emailVerified`).
export const provedMobileOnly = (state = {}) => isVerified(state) && !state.viaGoogle && state.emailVerified === false;

// The code goes by text when a mobile number is given, else by email. The
// server still sends it to the email when Twilio is off or refuses, and
// when only an email code can win the email back from an older account.
export const codeChannelFor = (phone) => (phone ? "sms" : "email");

const CREATE_FAILED = "We could not create your account. Try again.";

// What to show for a refused /register/: the server's own sentence (its
// message first, then a field's), or a plain line for a fault.
const refusalMessage = (status, data) => {
  if (status >= 500 || !data || typeof data !== "object") return CREATE_FAILED;
  const firstField = Object.values(data).flat()[0];
  return sentence(data.message) || sentence(data.detail) || sentence(data.error) || sentence(firstField) || CREATE_FAILED;
};

// The ticket lasts 45 minutes. Google sent no code, so a Google sign-up
// signs in with Google again; a typed email or mobile number confirms a
// fresh code on the email step, which comes back filled in.
const restartVerification = (identity, roleKey, navigate) => {
  if (identity.viaGoogle) {
    clearSignup();
    writeSignup({ accountType: roleKey });
    toast.info("Your Google sign-in timed out. Continue with Google again.");
    navigate("/welcome", { replace: true });
    return;
  }
  writeSignup({ ticket: "" });
  toast.info("Your verification expired. We will send you a new code.");
  navigate("/signup/email", { replace: true });
};

// Creates the account of type `roleKey` from the signup identity (the
// verified ticket, the names, the typed password or a generated one for
// Google, the phone) plus `extra` (the athlete's birthday and guardian, a
// rep's agency), starts the session, clears the tab's signup state and
// moves on: athletes and coaches to onboarding, the others to their home.
// Returns null when the page has moved on, else { message, nameProblem }.
export const registerFromSignup = async ({ identity, roleKey, extra = {}, dispatch, navigate }) => {
  const password = identity.password || (identity.viaGoogle ? generatedPassword() : "");
  const { ok, status, data } = await registerAccount({
    email: identity.email,
    password,
    password2: password,
    firstName: identity.firstName,
    lastName: identity.lastName,
    username: `${identity.firstName}${identity.lastName}`,
    role: routePathsByRole[roleKey].value,
    eventProgram: [],
    signupTicket: identity.ticket,
    phone: identity.phone || "",
    ...extra,
  });
  if (!ok) {
    if (data?.error === "verification_required") {
      restartVerification(identity, roleKey, navigate);
      return null;
    }
    const nameProblem = data?.fields?.firstName || data?.fields?.lastName;
    if (nameProblem) return { message: sentence([].concat(nameProblem)[0]) || sentence(data.message) || "Check your name.", nameProblem: true };
    return { message: refusalMessage(status, data) };
  }
  // /register/ answers a ticketed sign-up with a session; without one the
  // account cannot go on.
  if (!data?.access) return { message: CREATE_FAILED };
  await startSession(data, dispatch);
  clearSignup();
  toast.success("Your account is ready.");
  navigate(accountTypeOf(roleKey).onboarding ? "/onboarding" : homePathFor(data.permissions || []), { replace: true });
  return null;
};
